import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import Avatar from "../../assets/images/avatar.png";
import { Input, Label, Spinner } from "reactstrap";
import { postAttachment } from "../../api/attachment";
import { CustomSnackbar } from "../../helpers/CustomSnackbar";
import { apiErrorHandler, dataUrl } from "../common"

const ImageUploader = forwardRef(({
  updateImageHandle,
  imageName,
  uploadPath = "/customers/attachment",
  assetType = "customers",
  placeholderImage = Avatar,
}, ref) => {
  const resolveDisplayUrl = (value) => {
    if (!value) return placeholderImage;
    if (value.startsWith("http")) return value;
    console.warn("⚠️ Received a bare filename instead of a full URL:", value);
    return placeholderImage;
  };

  // never append a query string to a data: or blob: URL —
  // only real http(s) URLs support cache-busting params.
  const withCacheBuster = (url) => {
    if (!url) return url;
    if (url.startsWith("http")) {
      return `${url}?t=${Date.now()}`;
    }
    return url;
  };

  const [localAttachment, setLocalAttachment] = useState(null);
  const [userPic, setUserPic] = useState(placeholderImage);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (imageName) {
      setUserPic(withCacheBuster(resolveDisplayUrl(imageName)));
    } else {
      setUserPic(placeholderImage);
    }
  }, [imageName, placeholderImage]);

  const handlePicUpload = (e) => {
    const file = e?.target?.files[0];
    if (!file) return;

    const allowedExt = ["jpg", "jpeg", "png"];
    const ext = file.name.split(".").pop().toLowerCase();

    if (!allowedExt.includes(ext)) {
      CustomSnackbar.error(`Error! Only ${allowedExt.join(", ")} file types are allowed.`);
      return;
    }

    if (file.size > 1500000) {
      CustomSnackbar.error("Error! File size must be less than 1.5 MB.");
      return;
    }

    // blob: URL, safe to set directly (no cache buster needed)
    setUserPic(URL.createObjectURL(file));
    setLocalAttachment(file);
  };

  // 🎯 THE FIX: no more standalone "Save" button here. The upload is now
  // exposed via ref so the PARENT form can trigger it (e.g. right before
  // the Create/Update API call), avoiding the separate failing request.
  const saveImage = () => {
    return new Promise((resolve, reject) => {
      if (!localAttachment) {
        resolve(null); // nothing to upload
        return;
      }

      const formData = new FormData();
      formData.append("file", localAttachment);
      formData.append("asset_type", assetType);

      setLoading(true);

      postAttachment(uploadPath, formData)
        .then((res) => {
          const parsed = typeof res?.data === "string" ? JSON.parse(res.data) : res?.data;

          const finalUrl =
            parsed?.data?.fileUrl ||
            parsed?.data?.company?.logo ||
            parsed?.fileUrl ||
            null;

          if (!finalUrl) {
            console.error("❌ Could not find a usable image URL in response:", parsed);
            CustomSnackbar.error("Upload succeeded but response format was unexpected.");
            reject(new Error("No usable image URL in response"));
            return;
          }

          updateImageHandle(finalUrl);
          setUserPic(withCacheBuster(finalUrl));
          setLocalAttachment(null);

          resolve(finalUrl);
        })
        .catch((err) => {
          console.error("❌ Upload request failed:", err);
          apiErrorHandler(err);
          reject(err);
        })
        .finally(() => setLoading(false));
    });
  };

  // 🎯 Standalone Save button handler — same saveImage() the ref exposes,
  // just triggerable directly from this component too.
  const handleSaveClick = () => {
    saveImage().catch(() => {
      // error already surfaced via CustomSnackbar/apiErrorHandler inside saveImage
    });
  };

  useImperativeHandle(ref, () => ({
    saveImage,
    hasPendingImage: !!localAttachment,
  }));

  return (
    <div className="text-center">
      <div className="mb-4 profile-user">
        <img
          src={userPic}
          className="rounded-circle img-thumbnail profile-img"
          id="profile-img"
          alt="Profile Preview"
        />

        {loading && (
          <Spinner size="sm" className="position-absolute top-50 start-50" />
        )}

        <div className="p-0 rounded-circle profile-photo-edit">
          <Input
            id="profile-img-file-input"
            type="file"
            className="profile-img-file-input"
            onChange={handlePicUpload}
            accept="image/*"
          />
          <Label htmlFor="profile-img-file-input" className="profile-photo-edit avatar-xs">
            <i className="uil uil-edit"></i>
          </Label>
        </div>
      </div>

      {/* 🎯 Save button — only shows when there's a newly picked image
          waiting to be uploaded */}
      {localAttachment && (
        <button
          type="button"
          className="btn btn-primary btn-sm mt-2"
          onClick={handleSaveClick}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-1" />
              Saving...
            </>
          ) : (
            <>
              <i className="uil uil-upload me-1"></i>
              Save Photo
            </>
          )}
        </button>
      )}
    </div>
  );
});

export default ImageUploader;